import { ViewChild } from '@angular/core';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { Observable, of, Subscription } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { ApplicationBaseComponent } from '../../../applications/components/application-base.component';
import { ApplicationState, ApplicationType, InitiateWorkflowRequest, WorkflowData, WorkflowNote, WorkflowFieldData } from '../../../applications/models/applications.models';
import { ApplicationNotesService } from '../../../applications/services/application-notes.service';
import { ApplicationsService } from '../../../applications/services/applications.service';
import { CanDeactivateComponent } from '../../../common/classes/can-deactivate.component';
import { NoteType } from '../../../common/models/note-type.enum';
import { OrganizationStub } from '../../../common/models/organization.model';
import { SidePanel } from '../../../common/models/side-panel.model';
import { WellLog, WellStub } from '../../../common/models/well.model';
import { IUploadFile } from '../../../common/services/document-management.service';
import { IssuesService } from '../../../common/services/issues.service';
import { LoaderService } from '../../../common/services/loader.service';
import { OrganizationService } from '../../../common/services/organization.service';
import { SidePanelService } from '../../../common/services/side-panel.service';
import { Dialog, DialogButton } from '../../../core/models/dialog.model';
import { SubHeader } from '../../../core/models/sub-header.model';
import { DynamicDialogService } from '../../../core/services/dynamic-dialog.service';
import { SubHeaderService } from '../../../core/services/sub-header.service';
import { AuthService } from '../../../services/auth.service';
import { ComplianceLogs, ComplianceReport, ComplianceReportingType, ComplianceReportStatus, ComplianceReportWell, FileProcessingMode, ReportingMethod, WellSelectionMode } from '../../models/compliance.model';
import { ElectricLogsComponent } from '../electric-logs/electric-logs.component';

@Component({
    selector: 'app-compliance-submissions',
    templateUrl: './compliance-submissions.component.html',
    styleUrls: ['./compliance-submissions.component.scss'],
    standalone: false
})
export class ComplianceSubmissionsComponent extends ApplicationBaseComponent implements OnInit {


    static saveConfirmedMessage: string = "Your report has been submitted for review.  For future reference, your pending report ID is [XXX].";
    static saveUnsubmittedMessage: string = "Your report has been saved for completion at a later date.  For future reference, this report is stored as ID [XXX].";
    approvedDialog: Dialog = new Dialog("This compliance report has been approved.", [new DialogButton("Home", false, () => { this.home(); })]);
    deniedDialog: Dialog = new Dialog("This compliance report has been denied.", [new DialogButton("Home", false, () => { this.home(); })]);
    reviewDialog: Dialog = new Dialog("This compliance report is currently being reviewed.", [new DialogButton("Home", false, () => { this.home(); })]);
    submittedDialog: Dialog = new Dialog("Application #[APPNUMBER] has been submitted for review.", [new DialogButton("OK", true, () => { this.home(); })]);
    orgConfirmDialog: Dialog = new Dialog("By confirming, you will not be able to change your selected organization without starting a new application.",
        [new DialogButton("Confirm", true, () => { this.onOrgConfirmed(); }), new DialogButton("Cancel", true, () => { })])
    saveUnsubmittedDialog: Dialog = new Dialog("", [new DialogButton("OK", false, () => { return true; })]);
    restrictedDialog: Dialog = new Dialog("You do not have permission to access this compliance report", [new DialogButton("Home", false, () => { this.home() })]);
    defaultType: ComplianceReportingType = { typeId: "", description: "--Select--", apiNumbers: WellSelectionMode.None, isAnnual: false, isQuarterly: false } as ComplianceReportingType
    doWellSearch: boolean = false;
    get appStatus(): string {
        if (this.application == null) return ApplicationState.Start;
        return this.application.applicationState;
	}

	get loadComplete(): boolean {
		if (this.dataLoaded == null) return false;
		return this.dataLoaded.value;
	}

    logsData: any = {
        ricsownerNumber: "",
        rbdmsNumber: "",
        orgName: "",
    }

    logsDueTypes: string[] = ["LOGD"];

    odnrOrgId: string[] = ["0002", "0003"];

    showODNRError: boolean = false;

    orgConfirmed: boolean = false;
    isSavedLog: boolean = false;
    application: WorkflowData;
    reportingMethods = ReportingMethod;
    reportTypes: ComplianceReportingType[] = [];
    subscription: Subscription = new Subscription();
	dataLoaded: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    orgSearchActive: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);;
    manualValidationStatus: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(null);
    fileModes = FileProcessingMode;
    _savedReport: ComplianceReport;
    _selectedReportType: ComplianceReportingType;
    _selectedOrganization: OrganizationStub;

    get applicationState(): ApplicationState {
        if (this.application == null) return ApplicationState.Start;
        return this.application.applicationState;
    }

    get isInCorrections(): boolean {
        return this.applicationState == ApplicationState.Corrections;
    }

    reviewStates: ApplicationState[] = [
        ApplicationState.VerifyCompletion,
        ApplicationState.InitialReview,
        ApplicationState.InitialApproval,
        ApplicationState.FinalApproval

    ];

    subHeader: SubHeader = new SubHeader({ title: "Compliance Reporting", buttons: [] });
    public reviewNotesSidePanel: SidePanel = new SidePanel({
        tabs: [{ title: "Notes" }, { title: "Timeline" }],
        sections: [
            { componentName: 'ApplicationNotes' }, { componentName: 'ApplicationTimeline' }
        ],
        visible: false
    });


    home(): void {
        this.router.navigate(["/home"]);
    }

    get apiNumber(): string {
        return this.logsData.apiNumber;
    }

    get isLogsDueType(): boolean {
        if (this.selectedReportType == null) return false;
        const applicable = this.logsDueTypes.find(s => this.selectedReportType.typeId === s);
        return applicable != null;
    }

    get organizationFound(): boolean {
        if (this.form == null) return false;
        const ctrl = this.form.get("organizationFound");
        if (ctrl == null) return false;
        return <boolean>ctrl.value;
    }


    get rbdmsNumber(): string {
        if (this.selectedOrganization == null) return "";
        if (this.selectedOrganization.historicalOwnerNumber != null) { return this.selectedOrganization.historicalOwnerNumber; }
        if (this.selectedOrganization.id.startsWith("ON") === false) { return this.selectedOrganization.id; }
        return "";
    }

    get reportType(): string {
        if (this.form == null) return "";
        let ctrl = this.form.get('reportType');
        if (ctrl == null) return ""
        return ctrl.value;
    }

    get ricsNumber(): string {
        if (this.selectedOrganization == null) return "";
        if (this.selectedOrganization.id.startsWith("ON")) return this.selectedOrganization.id;
        return "N/A";
    }

    get showReport(): boolean {
        return this.organizationFound && this.dataLoaded.value == true && this.orgConfirmed;;
    }

    get selectedOrganization(): OrganizationStub { return this._selectedOrganization; }

    set selectedOrganization(value: OrganizationStub) { this._selectedOrganization = value; }

    get selectedReportType(): ComplianceReportingType {
        let returnValue = this._selectedReportType;
        if (this._selectedReportType == null) {
            if (this.form != null && this.reportTypes != null) {
                let idCtrl = this.form.get("reportType");
                if (idCtrl == null) return null;
                this._selectedReportType = this.reportTypes.find(r => r.typeId == idCtrl.value);
            }
            returnValue = this._selectedReportType;
        }
        return returnValue;
    }

    set selectedReportType(value: ComplianceReportingType) {
        this._selectedReportType = value;
    }

    get savedReport(): ComplianceReport {
        return this._savedReport;
    }

    set savedReport(value: ComplianceReport) {
        this._savedReport = value;
    }

    get wellSelectionMode(): WellSelectionMode {
        if (this.selectedReportType == null) return WellSelectionMode.None;
        return this.selectedReportType.apiNumbers;
    }



    
    constructor(public loader: LoaderService, private activatedRoute: ActivatedRoute, public router: Router, auth: AuthService, dialogService: DynamicDialogService,
        private issuesService: IssuesService, private builder: FormBuilder, private organizationService: OrganizationService, public appService: ApplicationsService, _notesService: ApplicationNotesService, _subheader: SubHeaderService, _sidePanelService: SidePanelService) {
        super(loader, dialogService, _subheader, auth, appService, router, _sidePanelService, _notesService);
    }

    ngOnInit() {
        super.ngOnInit();
        this.form = this.buildForm();

        if (this.auth.User.isInternal == false) {
            this.form.get('organization').setValue(this.auth.User.organizationId);
            this.searchOrganizations(true);
            this.onOrgConfirmed();
        }
        
        let typeOb$ = this.issuesService.getComplianceReportingTypes(true).pipe(
            tap((data: ComplianceReportingType[]) => {
                let sorted = data.sort((a, b) => {
                    if (a.description > b.description) return 1;
                    if (b.description > a.description) return -1;
                    return 0;
                });
                sorted.forEach(s => { this.reportTypes.push(s); });
                this.reportTypes.unshift(this.defaultType);
   
            })
        )

        this.loadData();

        let subs$ = typeOb$.pipe().subscribe(
            (data: any) => { },
            (error) => { this.logError(error); }
        )

        this.subscription.add(subs$);
       

    
    }

    buildForm(): FormGroup {
        let defaultOrg = this.auth.User.isInternal ? "" : this.auth.User.organizationId;
        let orgName: string = "";
        return this.builder.group({
            reportId: ["", []],
            reportType: ["", [Validators.required]],
            organization: [{ value: defaultOrg, disabled: !this.auth.User.isInternal }, [Validators.required]],
            organizationFound: [this.auth.User.isInternal == false, [Validators.requiredTrue]],
            organizationName: [{ value: orgName, disabled: !this.auth.User.isInternal }, [Validators.required]],
            orgSearchRun: [this.auth.User.isInternal == false, [Validators.requiredTrue]],
            uploadedFiles: [[], []],
            wellList: [[], []],
            submitType: []
        });
    }


    buildRequest(): InitiateWorkflowRequest {
        return {
            organizationId: this.form.get('organization').value,
            applicationType: ApplicationType.ComplianceReporting
        } as InitiateWorkflowRequest
    }

    deactivate(doDeactivate: boolean): void {
        this.allowDeactivate.next(doDeactivate);
    }


    loadData() {
        let id: any = this.activatedRoute.snapshot.params.id;

        if (id != null) {
            let obs$ = this.appService.getApplicationByApplicationNumber(id);
            let subs$ = obs$.pipe(
                tap((data: WorkflowData) => {
                    if (data != null) {
                        this.form.get("reportId").setValue(data.applicationNumber);
                        this.form.get("reportType").setValue(data.workflowMetadata.reportType);
                        this.application = data;
                        if (this.isInCorrections) this.form.get('reportType').disable();
                        switch (data.workflowMetadata.reportType) {
                            case "LOGD":
                                this.onOrgConfirmed();
                                this.isSavedLog = true;
                                this.restoreLogData(data.workflowFieldDatas);                               
                                break;
                            case "PLUG":
                                this.onOrgConfirmed();
                                this.isSavedLog = true;
                                this.restoreLogData(data.workflowFieldDatas);
                                break;
                            case "F8B":
                                this.onOrgConfirmed();
                                this.isSavedLog = true;
                                this.restoreLogData(data.workflowFieldDatas);
                                break;
                            case "F8A":
                                this.onOrgConfirmed();
                                this.isSavedLog = true;
                                this.restoreLogData(data.workflowFieldDatas);
                                break;
                            case "F8":
                                this.onOrgConfirmed();
                                this.isSavedLog = true;
                                this.restoreLogData(data.workflowFieldDatas);
                                break;
                            case "F8F":
                                this.onOrgConfirmed();
                                this.isSavedLog = true;
                                this.restoreLogData(data.workflowFieldDatas);
                                break;
                            case "ADS":
                                this.onOrgConfirmed();
                                this.isSavedLog = true;
                                this.restoreLogData(data.workflowFieldDatas);
                                break;

                            default:
                                break;
                        }                         

                    }
                })
            )
                .subscribe(
                    () => {
                        this.dataLoaded.next(true);
                        if (this.application != null && (this.reviewStates.includes(this.application.applicationState) || this.application.applicationState == ApplicationState.Corrections)) {
                            this.subHeader.buttons.push({ name: "View Notes & Timeline", method: () => this.viewTimeline() });
                        }
                        this.subheaderService.updateSubHeader(this.subHeader);
                    }
                )
            this.subscription.add(subs$);
        } else {
            this.subheaderService.updateSubHeader(this.subHeader);
            this.dataLoaded.next(true);
        }


    }


    passLogData(): void {
        this.logsData.id = this.form.get('reportId').value
        this.logsData.orgName = this.form.get('organizationName').value
        this.logsData.rbdmsNumber = this.rbdmsNumber;
        this.logsData.ricsownerNumber = this.ricsNumber;
    }

    restoreLogData(fields: WorkflowFieldData[]): void {
        for (let i = 0; i < fields.length; i++) {

            if (fields[i].formControlName == 'rbdmsNumber') {
                this.form.get("organization").setValue(fields[i].stringData);
                this.searchOrganizations(true);
            }

            if (fields[i].formControlName == 'apiNumber') {
                this.logsData.apiNumber = fields[i].stringData;
                this.form.get("wellList").setValue(fields[i].stringData);

                this.doWellSearch = true;

            }

            if (fields[i].formControlName == 'confidentiality') {
                this.logsData.confidential = fields[i].booleanData;

            }

            if (fields[i].formControlName == 'typeOfLog') {
                this.logsData.logType = fields[i].stringData

            }

            if (fields[i].formControlName == 'loggingCompany') {
                this.logsData.logCompany = fields[i].stringData

            }



        }
    }
   

    getOrganizationSearchObservable(useId: boolean): Observable<OrganizationStub> {
        let obs$ = (useId)
            ? this.searchById()
            : this.searchByName();
        return obs$.pipe(
            map((matchingOrgs: OrganizationStub[]) => {
                let rics = matchingOrgs.filter(m => m.id.startsWith("ON"));
                if (rics.length > 0) {
                    let registered = rics.filter(r => r.hasRegistered);
                    if (registered.length > 0) {
                        this.selectedOrganization = registered[0];
                    } else {
                        this.selectedOrganization = rics[0];
                    }
                } else if (matchingOrgs.length > 0) {
                    this.selectedOrganization = matchingOrgs[0];
                }
                this.orgSearchActive.next(false);
                this.allowDeactivate.next(this.selectedOrganization == null);
                return this.selectedOrganization;
            }
            ),
            tap((org: OrganizationStub) => {
                const found = this.form.get("organizationFound");
                if (found != null) {
                    found.setValue(org != null);
                }
                const idCtrl = this.form.get("organization");
                if (idCtrl != null && (idCtrl.value == null || idCtrl.value.length === 0)) {
                    let id = (org != null) ? org.id : "";
                    idCtrl.patchValue(id, { emitEvent: false });
                }
                const nameCtrl = this.form.get("organizationName");
                if (nameCtrl != null && (nameCtrl.value == null || nameCtrl.value.length === 0)) {
                    let name = (org != null) ? org.name : "";
                    nameCtrl.patchValue(name, { emitEvent: false });
                }
            }));
    }

    searchOrganizations(useId: boolean): void {
        const searchCtrl = this.form.get("orgSearchRun");
        this.orgSearchActive.next(true);
        const obs$ = this.getOrganizationSearchObservable(useId);
        const searchSub$ = obs$
            .pipe(
                finalize(() => { searchCtrl.setValue(true); })
            )
            .subscribe(
                (org: OrganizationStub) => {
                    if (this.reportType != "") this.evaluateWellSearch();                    
                },
                (error) => {
                    this.logError(error);
                    this.orgSearchActive.next(false);
                });
        this.subscription.add(searchSub$);

    }

    searchById(): Observable<OrganizationStub[]> {
        const ctrl = this.form.get("organization");
        let obs$: Observable<OrganizationStub[]>;
        if (ctrl != null) {
            const orgId = ctrl.value;
            if (this.odnrOrgId.includes(orgId)) {
                this.showODNRError = true;
                obs$ = of([]);
            } else {
                obs$ = this.organizationService.getOrganizationStubs([orgId]);
            }
        } else {
            obs$ = of([]);
        }
        return obs$;
    }

    searchByName(): Observable<OrganizationStub[]> {
        const ctrl = this.form.get("organizationName");
        let obs$: Observable<OrganizationStub[]>;
        if (ctrl != null) {
            obs$ = this.organizationService.getOrganizationStubsByPartialName(ctrl.value);
        } else {
            obs$ = of([]);
        }
        return obs$;
    }

    resetOrganization(): void {
        if (this.auth.User.isInternal) {
            const idCtrl = this.form.get("organization");
            if (idCtrl != null) {
                idCtrl.setValue("");
            }
            const nameCtrl = this.form.get("organizationName");
            if (nameCtrl != null) {
                nameCtrl.setValue("");
            }
            this.selectedOrganization = undefined;
        }
        const foundCtrl = this.form.get("organizationFound");
        if (foundCtrl != null) {
            foundCtrl.setValue(this.selectedOrganization != null);
        }
        const searchRunCtrl = this.form.get("orgSearchRun");
        if (searchRunCtrl != null) {
            searchRunCtrl.setValue(this.selectedOrganization != null);
        }
     
        const reportingMethodCtrl = this.form.get("reportingMethod");
        if (reportingMethodCtrl != null) reportingMethodCtrl.setValue("");
        this.form.updateValueAndValidity();
        this.manualValidationStatus.next(null);
    }

    confirmOrg() {
        const dialog: Dialog = this.orgConfirmDialog;
        dialog.toggleDialog(true);
        this.dialogService.updateDialog(dialog);
    }

    onOrgConfirmed() {
        this.orgConfirmed = true;
        this.form.controls["organization"].disable()
        this.form.controls["organizationName"].disable()
        this.passLogData()


    }

    logError(error: any): void {
        console.error(error);
    }

    handleError(error: any): Observable<any> {
        return of(error).pipe(tap((data) => {
            console.error(error);
            this.loader.toggleLoader(false);
        }));
    }

    evaluateWellSearch(): void {
        const reportType = this.form.get("reportType").value;
        this.selectedReportType = this.reportTypes.find(t => t.typeId === reportType);
        const organizationFound = <boolean>this.form.get("organizationFound").value;
        this.doWellSearch = (this.wellSelectionMode !== WellSelectionMode.None && organizationFound);
        const listCtrl = this.form.get("wellList");

        if (listCtrl != null) {
            const validatorList: any[] = [];
            if (this.doWellSearch) {
                validatorList.push(Validators.required);
                validatorList.push(Validators.minLength(1));
                if (this.wellSelectionMode !== WellSelectionMode.Multiple) {
                    validatorList.push(Validators.maxLength(1));
                }
            }
            if (validatorList.length > 0) {
                listCtrl.setValidators(validatorList);
            } else {
                listCtrl.clearValidators();
            }
            listCtrl.updateValueAndValidity();
        }
    }

    reportTypeChanged(event: any) {
        this.allowDeactivate.next(event === this.defaultType.typeId);
        this.evaluateWellSearch();    

    }

    hasFieldNotes(fieldName: string): boolean {
        return this.getFilteredNotes(fieldName).length > 0;
    }
   


}
